<template>
  <div class="email-page">

    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>邮件通知</h2>
        <p class="subtitle">配置 SMTP 服务器和商品邮件通知规则</p>
      </div>
    </div>

    <!-- Tab 切换 -->
    <div class="tab-nav">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab-btn', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key"
      >
        <span class="tab-icon" v-html="tab.icon"></span>
        {{ tab.label }}
      </button>
    </div>

    <!-- ══ SMTP 配置 ══ -->
    <div v-if="activeTab === 'smtp'" class="tab-content">
      <div class="config-card">
        <div class="card-head">
          <h3>SMTP 服务器配置</h3>
          <p class="card-desc">配置邮件发送服务，163 邮箱需开启 POP3/SMTP 并获取授权码</p>
        </div>

        <div class="card-body">
          <div class="form-grid">
            <div class="form-field">
              <label>SMTP 服务器</label>
              <input v-model="smtpForm.smtp_host" type="text" placeholder="smtp.163.com" />
            </div>
            <div class="form-field form-field-sm">
              <label>端口</label>
              <input v-model.number="smtpForm.smtp_port" type="number" placeholder="465" />
            </div>
            <div class="form-field form-field-sm">
              <label>SSL</label>
              <label class="switch">
                <input type="checkbox" v-model="smtpForm.smtp_secure" />
                <span class="slider"></span>
              </label>
              <span class="switch-hint">{{ smtpForm.smtp_secure ? 'SSL (465)' : 'TLS (587)' }}</span>
            </div>
            <div class="form-field">
              <label>发件邮箱</label>
              <input v-model="smtpForm.smtp_user" type="email" placeholder="yourname@163.com" />
            </div>
            <div class="form-field">
              <label>授权码 <span class="field-hint">（不是登录密码，是授权码）</span></label>
              <input v-model="smtpForm.smtp_pass" type="password" placeholder="填写则更新授权码，留空则保持不变" />
              <div v-if="smtpSavedPass" class="pass-mask">已保存 ········</div>
            </div>
            <div class="form-field">
              <label>发件人名称</label>
              <input v-model="smtpForm.from_name" type="text" placeholder="化工小助手" />
            </div>
          </div>
        </div>

        <div class="card-foot">
          <div class="foot-left">
            <button class="btn btn-secondary" @click="testEmailDialog = true" :disabled="smtpLoading">
              发送测试邮件
            </button>
          </div>
          <div class="foot-right">
            <button class="btn btn-secondary" @click="fetchSmtpConfig" :disabled="smtpLoading">
              <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
                <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              重置
            </button>
            <button class="btn btn-primary" @click="saveSmtpConfig" :disabled="smtpLoading">
              <svg v-if="smtpLoading" class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
                <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              {{ smtpLoading ? '保存中...' : '保存配置' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 163邮箱帮助 -->
      <div class="help-card">
        <h4>163 邮箱配置说明</h4>
        <ol>
          <li>登录 <strong>mail.163.com</strong> → 设置 → POP3/SMTP/IMAP</li>
          <li>开启 <strong>POP3/SMTP 服务</strong></li>
          <li>点击"授权码管理" → 生成授权码（不是登录密码）</li>
          <li>将授权码填入上方"授权码"字段</li>
          <li>保存后点击"发送测试邮件"验证配置是否正确</li>
        </ol>
      </div>
    </div>

    <!-- ══ 邮件通知规则 ══ -->
    <div v-if="activeTab === 'product'" class="tab-content">
      <div class="toolbar">
        <div class="toolbar-left">
          <h3>邮件通知规则</h3>
          <span class="count-badge">{{ configs.length }} 条</span>
        </div>
        <button class="btn btn-primary" @click="openAddDialog">
          <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          新增规则
        </button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="state-box loading">
        <div class="spinner"></div>
        <span>加载配置中...</span>
      </div>

      <!-- 空状态 -->
      <div v-else-if="configs.length === 0" class="state-box empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
        </svg>
        <p>暂无通知规则</p>
        <span>点击上方按钮添加邮件通知规则</span>
      </div>

      <!-- 配置列表 -->
      <div v-else class="config-table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>通知邮箱</th>
              <th>订阅商品</th>
              <th>备注</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cfg in configs" :key="cfg.id">
              <td class="email-cell">{{ cfg.notify_email }}</td>
              <td class="products-cell">
                <div class="product-tags">
                  <span
                    v-for="code in getCodes(cfg.product_codes)"
                    :key="code"
                    class="product-tag"
                    :class="{ inactive: !isProductActive(code) }"
                    :title="getProductName(code)"
                  >
                    {{ getProductName(code) || code }}
                  </span>
                </div>
              </td>
              <td class="remark-cell">{{ cfg.remark || '-' }}</td>
              <td>
                <span :class="['badge', cfg.enabled ? 'ok' : 'muted']">
                  {{ cfg.enabled ? '已启用' : '已禁用' }}
                </span>
              </td>
              <td class="time-cell">{{ formatTime(cfg.created_at) }}</td>
              <td>
                <button class="btn-link" @click="openEditDialog(cfg)">编辑</button>
                <button class="btn-link danger" @click="confirmDelete(cfg)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showDialog" class="modal-overlay" @click.self="showDialog = false">
          <div class="modal-box">
            <div class="modal-head">
              <h3>{{ editingCfg ? '编辑规则' : '新增规则' }}</h3>
              <button class="modal-x" @click="showDialog = false">&times;</button>
            </div>
            <div class="modal-body">
              <div class="form-field">
                <label>通知邮箱 <span class="required">*</span></label>
                <input v-model="form.notify_email" type="email" placeholder="admin@example.com" />
              </div>
              <div class="form-field">
                <label>订阅商品 <span class="required">*</span> <span class="field-hint">（可多选）</span></label>
                <div class="product-checkboxes">
                  <label
                    v-for="p in products"
                    :key="p.product_code"
                    :class="['product-check-item', { selected: form.product_codes.includes(p.product_code) }]"
                  >
                    <input
                      type="checkbox"
                      :value="p.product_code"
                      v-model="form.product_codes"
                    />
                    <span>{{ p.name }}</span>
                    <code class="check-code">{{ p.product_code }}</code>
                  </label>
                </div>
                <div v-if="form.product_codes.length > 0" class="selected-hint">
                  已选 {{ form.product_codes.length }} 个商品
                </div>
                <div v-else class="selected-hint empty">请至少选择一个商品</div>
              </div>
              <div class="form-field">
                <label>备注</label>
                <input v-model="form.remark" type="text" placeholder="可选备注信息" />
              </div>
              <div class="form-field">
                <label>启用</label>
                <label class="switch">
                  <input type="checkbox" v-model="form.enabled" />
                  <span class="slider"></span>
                </label>
                <span class="switch-hint-inline">{{ form.enabled ? '支付成功后发送邮件' : '暂停通知' }}</span>
              </div>
            </div>
            <div class="modal-foot">
              <button class="btn btn-secondary" @click="showDialog = false">取消</button>
              <button class="btn btn-primary" @click="saveConfig" :disabled="dialogLoading">
                <svg v-if="dialogLoading" class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
                  <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
                {{ dialogLoading ? '保存中...' : '保存' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 测试邮件弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="testEmailDialog" class="modal-overlay" @click.self="testEmailDialog = false">
          <div class="modal-box modal-sm">
            <div class="modal-head">
              <h3>发送测试邮件</h3>
              <button class="modal-x" @click="testEmailDialog = false">&times;</button>
            </div>
            <div class="modal-body">
              <div class="form-field">
                <label>收件人邮箱 <span class="required">*</span></label>
                <input v-model="testEmail" type="email" placeholder="请输入测试收件邮箱" />
              </div>
            </div>
            <div class="modal-foot">
              <button class="btn btn-secondary" @click="testEmailDialog = false">取消</button>
              <button class="btn btn-primary" @click="sendTestEmail" :disabled="testLoading">
                <svg v-if="testLoading" class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
                  <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
                {{ testLoading ? '发送中...' : '发送' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 错误提示 -->
    <Transition name="toast-anim">
      <div v-if="errorMsg" :class="['toast', toastType]">
        <svg v-if="toastType === 'error'" viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <svg v-else viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        {{ errorMsg }}
        <button @click="errorMsg = ''">×</button>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'

const API_BASE = import.meta.env.VITE_API_BASE || ''

// ── Tab ──
const tabs = [
  {
    key: 'smtp',
    label: 'SMTP 配置',
    icon: '<svg viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>',
  },
  {
    key: 'product',
    label: '商品邮件配置',
    icon: '<svg viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/></svg>',
  },
]
const activeTab = ref('smtp')

// ── SMTP ──
const smtpForm = reactive({
  smtp_host: 'smtp.163.com',
  smtp_port: 465,
  smtp_secure: true,
  smtp_user: '',
  smtp_pass: '',
  from_name: '化工小助手',
})
const smtpFormOrigin = reactive({})
const smtpLoading = ref(false)
const smtpSavedPass = ref(false)

function getToken() {
  return localStorage.getItem('admin_token')
}

async function adminFetch(url, options = {}) {
  const token = getToken()
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    window.location.reload()
    throw new Error('Unauthorized')
  }
  return res
}

async function fetchSmtpConfig() {
  smtpLoading.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/email/config`)
    const data = await res.json()
    if (data.success && data.config) {
      smtpForm.smtp_host = data.config.smtp_host || 'smtp.163.com'
      smtpForm.smtp_port = data.config.smtp_port || 465
      smtpForm.smtp_secure = !!data.config.smtp_secure
      smtpForm.smtp_user = data.config.smtp_user || ''
      smtpForm.smtp_pass = ''
      smtpForm.from_name = data.config.from_name || '化工小助手'
      smtpSavedPass.value = !!data.config.smtp_pass
      Object.assign(smtpFormOrigin, smtpForm)
    } else {
      smtpForm.smtp_host = 'smtp.163.com'
      smtpForm.smtp_port = 465
      smtpForm.smtp_secure = true
      smtpForm.smtp_user = ''
      smtpForm.smtp_pass = ''
      smtpForm.from_name = '化工小助手'
      smtpSavedPass.value = false
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('加载配置失败: ' + e.message, 'error')
  } finally {
    smtpLoading.value = false
  }
}

async function saveSmtpConfig() {
  if (!smtpForm.smtp_host || !smtpForm.smtp_user) {
    showToast('SMTP 服务器和发件邮箱不能为空', 'error')
    return
  }
  smtpLoading.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/email/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(smtpForm),
    })
    const data = await res.json()
    if (data.success) {
      smtpSavedPass.value = true
      smtpForm.smtp_pass = ''
      showToast('SMTP 配置已保存', 'success')
    } else {
      showToast(data.message || '保存失败', 'error')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('保存失败: ' + e.message, 'error')
  } finally {
    smtpLoading.value = false
  }
}

// ── 邮件通知规则 ──
const configs = ref([])
const products = ref([])
const loading = ref(false)
const showDialog = ref(false)
const editingCfg = ref(null)
const dialogLoading = ref(false)
const form = reactive({
  notify_email: '',
  product_codes: [],
  enabled: true,
  remark: '',
})

async function fetchConfigs() {
  loading.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/email/product-configs`)
    const data = await res.json()
    if (data.success) {
      configs.value = data.configs || []
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('加载配置失败: ' + e.message, 'error')
  } finally {
    loading.value = false
  }
}

function getCodes(productCodes) {
  return (productCodes || '').split(',').map(s => s.trim()).filter(Boolean)
}

function getProductName(code) {
  const p = products.value.find(p => p.product_code === code)
  return p ? p.name : ''
}

function isProductActive(code) {
  const p = products.value.find(p => p.product_code === code)
  return p ? !!p.is_active : false
}

async function fetchProducts() {
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/products`)
    const data = await res.json()
    if (data.success) {
      products.value = data.products || []
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') console.error('[Email] Load products error:', e)
  }
}

function openAddDialog() {
  editingCfg.value = null
  form.notify_email = ''
  form.product_codes = []
  form.enabled = true
  form.remark = ''
  showDialog.value = true
}

function openEditDialog(cfg) {
  editingCfg.value = cfg
  form.notify_email = cfg.notify_email
  form.product_codes = getCodes(cfg.product_codes)
  form.enabled = !!cfg.enabled
  form.remark = cfg.remark || ''
  showDialog.value = true
}

async function saveConfig() {
  if (!form.notify_email) {
    showToast('通知邮箱不能为空', 'error')
    return
  }
  if (form.product_codes.length === 0) {
    showToast('请至少选择一个商品', 'error')
    return
  }
  dialogLoading.value = true
  try {
    const url = editingCfg.value
      ? `${API_BASE}/api/admin/email/product-config/${editingCfg.value.id}`
      : `${API_BASE}/api/admin/email/product-config`
    const method = editingCfg.value ? 'PUT' : 'POST'
    const res = await adminFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
    const data = await res.json()
    if (data.success) {
      showDialog.value = false
      showToast(editingCfg.value ? '规则已更新' : '规则已添加', 'success')
      fetchConfigs()
    } else {
      showToast(data.message || '保存失败', 'error')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('保存失败: ' + e.message, 'error')
  } finally {
    dialogLoading.value = false
  }
}

async function confirmDelete(cfg) {
  if (!confirm(`确认删除「${cfg.notify_email}」的通知规则？`)) return
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/email/product-config/${cfg.id}`, {
      method: 'DELETE',
    })
    const data = await res.json()
    if (data.success) {
      showToast('规则已删除', 'success')
      fetchConfigs()
    } else {
      showToast(data.message || '删除失败', 'error')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('删除失败: ' + e.message, 'error')
  }
}

// ── 测试邮件 ──
const testEmailDialog = ref(false)
const testEmail = ref('')
const testLoading = ref(false)

async function sendTestEmail() {
  if (!testEmail.value) {
    showToast('请输入收件邮箱', 'error')
    return
  }
  testLoading.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/email/config/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sendTo: testEmail.value }),
    })
    const data = await res.json()
    if (data.success) {
      testEmailDialog.value = false
      showToast(data.message, 'success')
    } else {
      showToast(data.message || '发送失败', 'error')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') showToast('发送失败: ' + e.message, 'error')
  } finally {
    testLoading.value = false
  }
}

// ── Toast ──
const errorMsg = ref('')
const toastType = ref('error')
let toastTimer = null

function showToast(msg, type = 'error') {
  errorMsg.value = msg
  toastType.value = type
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { errorMsg.value = '' }, 3000)
}

function formatTime(t) {
  if (!t) return '-'
  try { return new Date(t).toLocaleString('zh-CN') } catch { return t }
}

onMounted(() => {
  fetchSmtpConfig()
  fetchConfigs()
  fetchProducts()
})
</script>

<style scoped>
.email-page {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}

/* 页面头部 */
.page-header {
  margin-bottom: 20px;
}
.header-left h2 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text, #111827);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-2, #6b7280);
  margin: 0;
}

/* Tab */
.tab-nav {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: var(--bg, #f3f4f6);
  border-radius: 12px;
  padding: 4px;
  width: fit-content;
}
.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-2, #6b7280);
  transition: all .15s;
  font-family: inherit;
}
.tab-btn:hover { color: var(--text, #111827); }
.tab-btn.active {
  background: var(--surface, #fff);
  color: var(--text, #111827);
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
}
.tab-icon { display: flex; align-items: center; }

/* 卡片 */
.config-card, .help-card, .config-table-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
  overflow: hidden;
}
.card-head {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
}
.card-head h3 { font-size: 16px; font-weight: 700; margin: 0 0 4px; }
.card-desc { font-size: 13px; color: var(--text-3, #9ca3af); margin: 0; }
.card-body { padding: 24px; }
.card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid var(--border-light, #f3f4f6);
  background: var(--bg, #f9fafb);
  flex-wrap: wrap;
  gap: 12px;
}
.foot-left, .foot-right { display: flex; gap: 8px; }

/* 表单 */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 200px;
  gap: 16px 20px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-field label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-2, #374151);
}
.form-field input, .form-field select {
  padding: 9px 13px;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 8px;
  font-size: 13.5px;
  color: var(--text, #111827);
  background: var(--surface, #fff);
  outline: none;
  transition: border-color .15s;
  font-family: inherit;
}
.form-field input:focus, .form-field select:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.08); }
.form-field input:disabled, .form-field select:disabled { background: var(--bg, #f3f4f6); color: var(--text-3, #9ca3af); cursor: not-allowed; }
.field-hint { font-weight: 400; color: var(--text-3, #9ca3af); }
.required { color: #dc2626; }
.pass-mask { font-size: 12px; color: var(--text-3, #9ca3af); }

/* 开关 */
.switch {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  width: 44px; height: 24px;
  background: var(--border, #d1d5db);
  border-radius: 24px;
  transition: all .2s;
  position: relative;
}
.slider::before {
  content: ''; position: absolute;
  width: 18px; height: 18px;
  left: 3px; bottom: 3px;
  background: #fff; border-radius: 50%;
  transition: all .2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
}
.switch input:checked + .slider { background: #4f46e5; }
.switch input:checked + .slider::before { transform: translateX(20px); }
.switch-hint { font-size: 12px; color: var(--text-3, #9ca3af); margin-left: 8px; }
.switch-hint-inline { font-size: 12px; color: var(--text-3, #9ca3af); }

/* 帮助卡片 */
.help-card {
  margin-top: 20px;
  padding: 20px 24px;
}
.help-card h4 { font-size: 14px; font-weight: 700; margin: 0 0 12px; color: var(--text, #111827); }
.help-card ol { margin: 0; padding-left: 20px; font-size: 13px; color: var(--text-2, #6b7280); line-height: 2; }

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.toolbar-left { display: flex; align-items: center; gap: 10px; }
.toolbar-left h3 { font-size: 16px; font-weight: 700; margin: 0; }
.count-badge {
  font-size: 11.5px; font-weight: 600;
  padding: 2px 10px; border-radius: 10px;
  background: var(--primary-light, #eef2ff); color: var(--primary, #4f46e5);
}

/* 状态框 */
.state-box { text-align: center; padding: 60px 0; color: var(--text-3, #9ca3af); font-size: 14px; background: var(--surface, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 14px; }
.state-box.loading { display: flex; align-items: center; justify-content: center; gap: 12px; }
.state-box.empty svg { width: 48px; height: 48px; margin: 0 auto 12px; display: block; color: var(--text-4, #d1d5db); }
.state-box.empty p { font-size: 16px; margin: 0 0 4px; color: var(--text-2, #6b7280); }
.state-box.empty span { font-size: 13px; }
.spinner { width: 22px; height: 22px; border: 2.5px solid var(--border, #e5e7eb); border-top-color: #4f46e5; border-radius: 50%; animation: spin .6s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* 表格 */
.config-table-card { overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th { text-align: left; padding: 11px 16px; font-size: 11.5px; font-weight: 600; color: var(--text-3, #9ca3af); text-transform: uppercase; letter-spacing: .04em; background: var(--bg, #f9fafb); border-bottom: 1px solid var(--border, #e5e7eb); white-space: nowrap; }
.data-table td { padding: 12px 16px; border-bottom: 1px solid var(--border-light, #f3f4f6); vertical-align: middle; }
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover td { background: rgba(79,70,229,.04); }
.product-cell { display: flex; flex-direction: column; gap: 2px; }
.product-code { font-family: 'Cascadia Code','Consolas',monospace; font-size: 12px; background: var(--bg, #f3f4f6); padding: 1px 6px; border-radius: 4px; color: #374151; }
.product-name { font-size: 12px; color: var(--text-3, #9ca3af); }
.email-cell { color: #374151; font-size: 13px; }
.remark-cell { color: var(--text-3, #9ca3af); font-size: 12px; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.time-cell { color: var(--text-3, #9ca3af); font-size: 12px; white-space: nowrap; }
.products-cell { max-width: 280px; }

.product-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.product-tag {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 6px; background: #eef2ff; color: #4f46e5; white-space: nowrap;
}
.product-tag.inactive { background: #f9fafb; color: #9ca3af; text-decoration: line-through; }

/* 商品多选 */
.product-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 6px;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 10px;
  max-height: 240px;
  overflow-y: auto;
}
.product-check-item {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 10px; border-radius: 6px;
  border: 1.5px solid var(--border, #e5e7eb);
  cursor: pointer; font-size: 13px; color: var(--text-2, #374151);
  transition: all .15s;
}
.product-check-item input[type="checkbox"] { width: 14px; height: 14px; flex-shrink: 0; accent-color: #4f46e5; }
.product-check-item:hover { border-color: #4f46e5; background: rgba(79,70,229,.04); }
.product-check-item.selected { border-color: #4f46e5; background: #eef2ff; color: #4f46e5; font-weight: 600; }
.check-code { font-size: 10px; color: var(--text-3, #9ca3af); margin-left: auto; font-family: 'Cascadia Code','Consolas',monospace; }
.product-check-item.selected .check-code { color: #818cf8; }
.selected-hint { font-size: 12px; color: #059669; font-weight: 600; margin-top: 4px; }
.selected-hint.empty { color: #dc2626; font-weight: 400; }

/* 徽章 */
.badge { display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 600; padding: 3px 10px; border-radius: 6px; letter-spacing: .02em; }
.badge.ok { background: #ecfdf5; color: #059669; }
.badge.muted { background: #f9fafb; color: #6b7280; }

/* 按钮 */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s; font-family: inherit;
  border: 1px solid transparent;
}
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn-primary { background: #4f46e5; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(79,70,229,.25); }
.btn-secondary { background: var(--surface, #fff); color: var(--text-2, #374151); border-color: var(--border, #e5e7eb); }
.btn-secondary:hover:not(:disabled) { border-color: #4f46e5; color: #4f46e5; }
.btn-link { background: none; border: none; color: #4f46e5; cursor: pointer; font-size: 12.5px; font-family: inherit; font-weight: 600; padding: 2px 6px; }
.btn-link:hover { text-decoration: underline; }
.btn-link.danger { color: #dc2626; }
.btn-link.danger:hover { text-decoration: underline; }
.spin { animation: spin 1s linear infinite; }

/* 弹窗 */
.modal-overlay { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,.45); backdrop-filter: blur(4px); padding: 20px; }
.modal-box { background: var(--surface, #fff); border-radius: 16px; width: 100%; max-width: 520px; max-height: 88vh; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,.25); display: flex; flex-direction: column; }
.modal-sm { max-width: 400px; }
.modal-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; border-bottom: 1px solid var(--border, #e5e7eb); flex-shrink: 0; }
.modal-head h3 { font-size: 16px; font-weight: 700; margin: 0; }
.modal-x { background: none; border: none; color: var(--text-3, #9ca3af); font-size: 24px; cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px; transition: all .15s; }
.modal-x:hover { background: var(--bg, #f3f4f6); color: var(--text, #111827); }
.modal-body { padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }
.modal-foot { display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid var(--border, #e5e7eb); background: var(--bg, #f9fafb); flex-shrink: 0; }

/* Toast */
.toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  padding: 10px 20px; border-radius: 10px; font-size: 13.5px; font-weight: 600;
  z-index: 99999; display: flex; align-items: center; gap: 8px;
  box-shadow: 0 8px 30px rgba(0,0,0,.15);
}
.toast.error { background: #dc2626; color: #fff; }
.toast.success { background: #059669; color: #fff; }
.toast button { background: none; border: none; color: inherit; font-size: 18px; cursor: pointer; padding: 0 4px; }

/* 动画 */
.modal-enter-active, .modal-leave-active { transition: opacity .2s ease; }
.modal-enter-active .modal-box, .modal-leave-active .modal-box { transition: transform .25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-box, .modal-leave-to .modal-box { transform: scale(.95) translateY(10px); }
.toast-anim-enter-active { transition: all .25s ease; }
.toast-anim-leave-active { transition: all .2s ease; }
.toast-anim-enter-from { opacity: 0; transform: translateX(-50%) translateY(16px); }
.toast-anim-leave-to { opacity: 0; transform: translateX(-50%) translateY(-8px); }
</style>
